import type { DomainLogEntry } from '../types';

/**
 * The domain layer's decision envelope — design §1.1's `Decision`, one layer down.
 *
 * BUILD-UNIT NOTE (U-2): §16 lists U-2's files without naming this one. It exists because
 * `machine.ts`, `board.ts` and `undo.ts` all reject, and three private copies of the same envelope
 * is how error handling drifts. Declared in the work state as an addition, not a silent one.
 *
 * Two properties are load-bearing:
 *
 * 1. **A rejection is a value, not an exception.** SES-1 requires illegal transitions to be
 *    "rejected in the domain layer, not merely hidden in the UI", and requires that rejection to be
 *    *observable*. A thrown error is observable only to whoever remembered to catch it; a returned
 *    `{ok:false, code}` is in the type of every call site.
 * 2. **A rejection cannot exist without its log entry.** `reject()` is the only constructor, and it
 *    builds the OBS-1 entry itself. There is no path that returns an error and forgets to log it,
 *    which is the second half of SES-1's sentence and the half that usually goes missing.
 */

/**
 * Typed error categories (NFR-8). A caller branches on the code, never on the message text.
 *
 * Codes are grouped by the module that raises them so the union reads as a map of what can go
 * wrong in the domain, which is the thing a reviewer actually wants to check for completeness.
 */
export type DomainErrorCode =
  // machine.ts — SES-1, SES-9, SES-5, DIST-1, INV-6
  | 'ERR_ILLEGAL_TRANSITION'
  | 'ERR_SETUP_IN_PROGRESS'
  | 'ERR_MATCH_IN_PROGRESS'
  | 'ERR_HALF_LENGTH_RANGE'
  | 'ERR_ANCHOR_REQUIRED'
  | 'ERR_ANCHOR_OUT_OF_RANGE'
  | 'ERR_UNRESOLVED_FLAGS'
  | 'ERR_TALKPOINT_SET'
  /**
   * `DeleteMatch` was refused because the match is **not abandoned** (Andrew's ruling, 2026-08-19:
   * *"limit the scope to abandoned and never-started matches only as eligible for deletion"*).
   *
   * A separate code from `ERR_ILLEGAL_TRANSITION`, which would be false here and would send a
   * reader hunting a state-machine fault: the transition is perfectly legal from this state, and it
   * is the row's own history that refuses it. An abandoned match and a played one awaiting its card
   * are both `fullTime`, so this is the one code that can tell the user which of the two they are
   * looking at.
   */
  | 'ERR_MATCH_NOT_ABANDONED'
  // board.ts — INV-4, INV-5, LOCK-6, LOCK-7, LIVE-8
  | 'ERR_BOARD_CAP'
  | 'ERR_BOARD_DUPLICATE'
  | 'ERR_BOARD_ALREADY_POSITIONED'
  | 'ERR_BOARD_NOT_POSITIONED'
  | 'ERR_BOARD_NOT_CONTIGUOUS'
  /**
   * LOCK-6 / HT-2 (U-13b) — a composition change was asked for on a board that does not exist yet.
   *
   * Distinct from `ERR_LOCK_NOT_PERMITTED`, which is the *opposite* complaint (a board that already
   * exists cannot be locked again). §7.4's HT-2 card is *"added, never substituted"*, so it is
   * reachable while still in Discovery — where the honest answer is HT-3's *"Lock my 8"*, not a
   * position assigned outside INV-4's *"exactly once per lock or per explicit user swap"*.
   */
  | 'ERR_BOARD_NOT_LOCKED'
  // undo.ts — LIVE-6
  | 'ERR_UNDO_NOT_IN_MATCH'
  | 'ERR_UNDO_WINDOW_CLOSED'
  | 'ERR_UNDO_NO_TARGET'
  // commands/* — build unit U-4. The snapshot is read inside the transaction (§1.1), so a row that
  // vanished between `pointerdown` and the commit is a normal outcome, not an exception: it returns
  // `{ok:false}` like any other rejection and nothing is acknowledged (SES-2).
  | 'ERR_MATCH_NOT_FOUND'
  | 'ERR_HYPOTHESIS_NOT_FOUND'
  /** The pre-built Observation row (§5.1 step 4) does not match the command it arrived on. */
  | 'ERR_COMMAND_ROW_MISMATCH'
  /** LOCK-4 / INV-4 — `canLock` refuses, so `boardPosition` cannot be assigned a second time. */
  | 'ERR_LOCK_NOT_PERMITTED'
  // setup.ts + commands/setup.ts — build unit U-7. PRE-1, PRE-3, PRE-4, PRE-7.
  /** PRE-4 — a stub was added with a blank still empty, or a blank the text does not contain. */
  | 'ERR_SLOT_UNRESOLVED'
  /** PRE-4 — a stub names two shirt numbers or two feet, so it has no single `Player` to bind to. */
  | 'ERR_SLOT_AMBIGUOUS'
  /** PRE-4 / ENV-11 — outside the number wheel's `[SHIRT_NUMBER_MIN, SHIRT_NUMBER_MAX]`. */
  | 'ERR_SHIRT_NUMBER_RANGE'
  /** PRE-1 — an opponent with no name is not selectable anywhere it would later have to be shown. */
  | 'ERR_TEAM_NAME_REQUIRED'
  /** PRE-3 — a custom hypothesis with no text. */
  | 'ERR_HYPOTHESIS_TEXT_REQUIRED'
  /**
   * PRE-1 / PRE-8 — a shape the match's own `format` does not offer (§6.0a).
   *
   * Covers both halves of one rule: a shape from another format's list, and *any* shape while no
   * format is set — before a format is chosen nothing is offerable, so nothing can be stored.
   */
  | 'ERR_FORMATION_UNKNOWN'
  /**
   * PRE-8 — the single clamp. Neither PRE-7 exit starts capture until `Match.format` is set.
   *
   * Raised by `handleStartCapture`, which is the one command behind both exits and the only route
   * out of `preMatch`. The disabled buttons on the screen are the courtesy; this is the mechanism.
   */
  | 'ERR_MATCH_FORMAT_REQUIRED'
  /** PRE-1 … PRE-7 — setup may only be edited, and capture only started, while `preMatch`. */
  | 'ERR_SETUP_NOT_PERMITTED'
  /** PRE-4 / FILE-2 — `[teamId+number]` already names a different `Player` row. */
  | 'ERR_PLAYER_NUMBER_TAKEN'
  /** PRE-1 — the match names a `Team` that does not exist. */
  | 'ERR_TEAM_NOT_FOUND'
  /** §3.1 — `Match.date` is a `YYYY-MM-DD` calendar date, never a timestamp. */
  | 'ERR_MATCH_DATE_INVALID'
  // commands/discovery.ts — build unit U-8. DISC-5.
  /** DISC-5 — a row may only be added *mid-Discovery*; the locked board's path is LOCK-6/HT-2. */
  | 'ERR_NOT_IN_DISCOVERY'
  /**
   * The match is not in an in-match state, so nothing captured now would have a place to live.
   *
   * Both directions of `isInMatch`, and build unit U-11 widened the second one rather than adding a
   * near-duplicate code. DISC-5's original reading was *"the match has finished"*; FLAG-1 and AUD-2
   * hit the same refusal from the other side, because PRE-7's exits hand the user a capture surface
   * while `Match.state` is still `preMatch` (§6.0) and §6.4a's negative space says a contact there
   * *"creates no Observation"*.
   */
  | 'ERR_MATCH_NOT_CAPTURING'
  /** DISC-5 — the id is already a hypothesis of this match. */
  | 'ERR_HYPOTHESIS_EXISTS'
  // lock.ts — build unit U-9b. LOCK-3.
  /** LOCK-3 — the review screen asked to swap out a hypothesis that is not in the selection. */
  | 'ERR_LOCK_NOT_SELECTED'
  // setPiece.ts — build unit U-12. SP-2 … SP-5.
  /**
   * §9 — the capture has already been closed (`draft` cleared by exiting the sheet) and a write
   * arrived for it anyway.
   *
   * Refused rather than applied, because a closed capture is the artifact DIST-4 attaches to a talk
   * point and CARD-1 renders: a late write from a surface that had not noticed the close would edit a
   * finished record. A new capture is the correct answer, and the sheet starts one.
   */
  | 'ERR_SET_PIECE_CLOSED'
  /**
   * SP-4 — the command changed a field the draft fixed when it was created.
   *
   * `matchMinute` is stamped via SES-10 *"at the moment the draft is created"*, and `type` decides the
   * whole marker vocabulary (SP-2 vs SP-3). A later tap that moved either would either re-time the
   * capture to whenever the user last touched it, or leave markers of a kind the new type has no
   * meaning for. Immutability here is what makes SP-4's "at the moment the draft is created" a
   * property of the data rather than of the surface resending the same number.
   */
  | 'ERR_SET_PIECE_IMMUTABLE'
  /**
   * SP-5 — a marker coordinate is not a finite number.
   *
   * `domain/setPiece.normalizeToBox` refuses to produce one, so this fires only when a row reached the
   * command without going through it. A `NaN` marker stores successfully and then fails to render —
   * on the sheet, and on CARD-1's share card months later — which is why the guard is on the data.
   */
  | 'ERR_SET_PIECE_GEOMETRY'
  // commands/halftime.ts + halftimeManage.ts + board.ts — build unit U-13. HT-5, HT-8, HT-2, PRE-3.
  /**
   * HT-5 / HT-8 / HT-2 / PRE-3 / LOCK-6 — the match is not in the `halftime` state.
   *
   * Deliberately **narrower** than `ERR_MATCH_NOT_CAPTURING`, which spans the whole in-match
   * grouping. A halftime adjustment or note written during the second half would be a
   * `phase='halftime'` artifact created after the break it names, and §17.6 asserts halftime rows
   * *"survive untouched through the second half"* — a claim with no content if the second half can
   * still add to them. Widening the existing code would have made that distinction invisible at the
   * one call site that depends on it.
   *
   * U-13b reuses it rather than minting a sibling. LOCK-6 confines composition changes to the break
   * for the same reason HT-5 confines adjustments to it, and PRE-3's halftime add is the same window
   * again — three requirements, one condition, so one renderable code.
   */
  | 'ERR_NOT_AT_HALFTIME'
  /**
   * HT-5 / INV-6 — more than `TALKPOINT_COUNT` adjustments were selected.
   *
   * The view's half of a bound `repos/talkPoints.writeSelection` also enforces inside the
   * transaction. Two checks, because they answer different questions: this one is renderable, the
   * repo's holds for callers that never came through a command (§11.9's restore).
   */
  | 'ERR_TALKPOINT_LIMIT'
  /** HT-5 — the same adjustment appeared twice in one selection, which `1..n` ranking cannot express. */
  | 'ERR_TALKPOINT_DUPLICATE'
  /** HT-8 — a *new* note with neither text nor audio. An **edit** to that state is legal: it clears one. */
  | 'ERR_NOTE_EMPTY'
  // commands/distill.ts — build unit U-14b. DIST-2, DIST-3, DIST-6, DIST-7, INV-13.
  /**
   * DIST-2 / §10.2 — a talk-point write arrived outside `Match.state === 'distill'`.
   *
   * Deliberately **not** `ERR_NOT_AT_HALFTIME`'s sibling and deliberately not shared with it: the two
   * windows are disjoint, and a message about the break on the post-match screen — or the reverse —
   * is the kind of lie `ERR_FLAGS_NOT_REVIEWABLE` was minted to stop telling. DIST-1 places
   * `PassTriage` before this window opens and §2.2's `Defer` is the way back out of it, so neither
   * end of the window is a trap.
   *
   * `ERR_TALKPOINT_LIMIT` and `ERR_TALKPOINT_DUPLICATE` are **reused** rather than duplicated: INV-6's
   * upper bound and the `1..n` ranking's unrepresentable-duplicate are one condition each, whichever
   * phase asked.
   */
  | 'ERR_NOT_AT_DISTILLATION'
  /**
   * DIST-3 / INV-13 — a `phase=fullTime` talk point arrived with blank instruction text.
   *
   * INV-13 is a compile error inside this tree (`FullTimeTalkPointRow.instructionText` is
   * non-optional) and an aborting `assertInv13` inside the transaction. This is the middle layer, and
   * the only one of the three the user can read: *"a share card with a blank point is not a talk
   * point"* said before the transaction rather than as a storage fault after it.
   */
  | 'ERR_INSTRUCTION_REQUIRED'
  /**
   * DIST-7 — a **source-less** talk point was authored on a match that is not sparse.
   *
   * *"When fewer than `TALKPOINT_COUNT` hypotheses exist for a match, the user may author a TalkPoint
   * with no source hypothesis … This is an escape hatch, not a primary flow."* The prominence half of
   * DIST-7 can only live on a surface; the **permission** half is enforceable, and design §4 is
   * explicit that an invariant enforced only by a UI is not one. A source-less point on a match that
   * tracked eight patterns discards the whole match's evidence and severs DIST-6's link that FILE-1
   * reads across matches.
   */
  | 'ERR_SOURCE_REQUIRED'
  /**
   * DIST-9 (revision 4) — the named hypothesis exists on this match and the match recorded **no
   * evidence** for it: `cachedCount = 0` and no user-set status override.
   *
   * A separate code from `ERR_HYPOTHESIS_NOT_FOUND`, which would be **false** here and would send a
   * reader hunting a data fault. Under PRE-9 a normal match carries ~43 such rows, so this is not an
   * exotic path: it is what a stale surface, a replayed command or an import reaches, and letting one
   * through would put an instruction about a pattern nobody observed onto the share card that is the
   * product's entire output (P-3). The user's route to saying it anyway is DIST-7's source-less talk
   * point, which is where an unevidenced instruction honestly belongs.
   */
  | 'ERR_HYPOTHESIS_UNEVIDENCED'
  // commands/flags.ts — build unit U-13c. FLAG-3, DIST-1.
  /**
   * FLAG-3 — the named row is not a `kind='flag'` Observation of this match.
   *
   * One code for three conditions (vanished · foreign match · wrong kind) because all three mean the
   * same thing to the user — *the flag you tapped is not there* — and the remedy for all three is to
   * reload the sheet. The structured data carries which it was, for OBS-2.
   */
  | 'ERR_FLAG_NOT_FOUND'
  /**
   * DIST-1 — *"a resolved flag MUST NOT reappear in the queue"*, said as a sentence rather than as a
   * `ConstraintError`.
   *
   * The unique `&observationId` index already makes a second resolution impossible; this exists so a
   * double-tap on a slow sheet reads as *"you already dealt with this one"* instead of as a storage
   * fault.
   */
  | 'ERR_FLAG_ALREADY_RESOLVED'
  // commands/flags.ts + commands/boundaries.ts — build unit U-14a. DIST-1, SES-8.
  /**
   * FLAG-3 / DIST-1 — the match is in a state where its flags are not reviewable.
   *
   * Replaces `ERR_NOT_AT_HALFTIME` on the flag path, which U-13c used while the window *was* the
   * break. §2.3 makes Triage a screen that changes no state, so a promote issued from it during a
   * dead spell arrives carrying `secondHalf` — and being told *"flags are reviewed during the
   * break"* while standing on the Triage screen at minute 60 would be a lie. The remaining negative
   * is `idle`/`preMatch` (nothing captured yet) and `archived` (settled before distillation), and
   * those read very differently, so the message branches on which while the code stays one.
   */
  | 'ERR_FLAGS_NOT_REVIEWABLE'
  /**
   * SES-8 / §10.1 — the boundary control fired outside `{fullTime, triage, distill}`.
   *
   * §17.1 records this as a *rendering* negative — *"the control is absent when the Triage screen is
   * reached from an in-match state"* — and §2.2 says why that is not enough on its own: *"the UI
   * additionally hides illegal controls, but the domain rejection is the enforcement."* This is the
   * enforcement. It is deliberately **not** `ERR_NOT_AT_HALFTIME`'s sibling: `halftime` is a
   * rejection here, because a boundary moved during the break moves the minute of a half still being
   * played.
   */
  | 'ERR_BOUNDARIES_NOT_ADJUSTABLE'
  /** SES-8 — an anchor arrived as something other than a finite timestamp. */
  | 'ERR_ANCHOR_INVALID'
  /**
   * SES-8 — an adjustment tried to **remove** an anchor the match already has.
   *
   * `stateAt` reads the anchors newest-first, so nulling `kickoff2Ts` on a match that played a second
   * half re-derives every second-half row as `halftime` — which SES-10 stamps at `halfLengthMin`,
   * collapsing a whole half onto one minute. Moving a boundary is the product action; deleting one
   * is not.
   */
  | 'ERR_ANCHOR_ERASED'
  /** SES-8 — the four anchors no longer partition the wall-clock line in lifecycle order. */
  | 'ERR_ANCHOR_ORDER'
  // commands/template.ts — build unit U-16. PRE-6.
  /**
   * PRE-6 / §6.0 — *Save board as template* fired outside `{fullTime, triage, distill, archived}`.
   *
   * The rendering negative (the action is absent from a running match's row in the Team view) is
   * what the user experiences; this is the enforcement §2.2 requires underneath it. Deliberately
   * **not** `ERR_SETUP_NOT_PERMITTED`'s sibling: that code means *"only while `preMatch`"* and this
   * one means the exact opposite, so sharing it would produce a message that is wrong in every
   * state that can reach either.
   */
  | 'ERR_TEMPLATE_NOT_POST_MATCH'
  /** PRE-6 — *"named"*, and a library entry with a blank name is not selectable in PRE-2's list. */
  | 'ERR_TEMPLATE_NAME_REQUIRED'
  /**
   * PRE-6 — the match has no positioned hypotheses, so the template would carry nothing.
   *
   * Refused rather than written: an empty `Template` appears in PRE-2's library, is selectable, and
   * adds zero patterns to the next board — a control that looks like it worked and did nothing.
   * Reachable on a match started in Discovery and never locked (LOCK-7 permits fewer than eight,
   * but §6.0's `discovery` exit assigns **no** position at all).
   */
  | 'ERR_TEMPLATE_EMPTY'
  /** PRE-6 — the minted id already names a library entry; a `put` would overwrite it. */
  | 'ERR_TEMPLATE_EXISTS'
  // commands/roster.ts — build unit B-2. B spec §4.2, scenario 2.
  /**
   * @deprecated **Retired by §15.3 / build unit B-25b — `OurPlayerRow.label` no longer exists.**
   *
   * Retired by *deprecation in place* rather than by deletion, and deliberately not by
   * reinterpreting a code whose name says `LABEL` to mean "first name": a reader meeting
   * `ERR_ROSTER_LABEL_REQUIRED` in a log written by an older bundle must still be able to look it
   * up and find what it meant. `ERR_ROSTER_IDENTITY_REQUIRED` is its successor, and it covers a
   * strictly wider rule (number **and** name).
   *
   * Nothing raises it. It stays in the union so the exhaustive `ERR_COPY` map (and any archived
   * log a support read might meet) keeps compiling.
   *
   * ~~B §4.1 — a roster row with no label is unrenderable and unpickable.~~
   */
  | 'ERR_ROSTER_LABEL_REQUIRED'
  /**
   * §3.7.36 — a roster row must carry **a jersey number and a first name**. Both, because both
   * are how the player is named downstream: the number is the referent §18's renderer emits, and
   * the name is what ruling R-1 keeps beside it (*"first names are used throughout youth sports…
   * that's not PII in any true meaning of PII"*).
   *
   * One code for both absences rather than two, because the sheet's Save is inert until *all* of
   * number, name and primary position are valid — so the only way to reach this is a stale or
   * programmatic caller, and it deserves one honest sentence rather than a field-by-field
   * dissection nobody will read. **Supersedes `ERR_ROSTER_LABEL_REQUIRED`.**
   */
  | 'ERR_ROSTER_IDENTITY_REQUIRED'
  /**
   * §3.7.36 — a roster row's positions do not describe a player this app can reason about.
   *
   * Covers three failures with one sentence because they are one mistake to the user: a
   * `secondaryPositions` list containing a duplicate, one containing the row's own
   * `primaryPosition`, or either field carrying a value outside {@link POSITIONS}. The type system
   * catches the last of these for callers inside this codebase; this code is what answers a stale
   * screen or an import-shaped caller that got past it.
   *
   * The primary-in-secondaries arm matters beyond tidiness: `secondaryPositions` is the **execution
   * filter**, and a list that repeats the primary would make "can also play" and "normally plays"
   * read as the same permission — which is exactly the conflation §3.7.36 draws a line under.
   */
  | 'ERR_ROSTER_POSITION_INVALID'
  /**
   * B §4.2 — a delete named a roster row that no longer exists. A normal outcome on a stale
   * screen (the roster is profile-scoped and editable from every session), answered as a value
   * so the sheet can say "already gone" rather than surface a storage fault.
   */
  | 'ERR_ROSTER_NOT_FOUND'
  // commands/annotations.ts — build unit B-2. B spec §4.2, scenario 17.
  /** B §4.2 — a new annotation with nothing in it. Blank text is a delete's job, not a write's. */
  | 'ERR_ANNOTATION_EMPTY'
  /**
   * B §4.2 — the annotation window is `firstHalf → distill`: never `preMatch` (nothing has been
   * observed, so there is nothing to annotate) and never `archived` (the record is closed).
   * Deliberately **not** `ERR_MATCH_NOT_CAPTURING`'s sibling — that code spans the in-match
   * states only, and an annotation is legal well past them (the match record keeps the entry
   * point through `distill`).
   */
  | 'ERR_ANNOTATIONS_NOT_WRITABLE'
  /**
   * B §4.2 — a delete named an annotation that does not exist **on this match**. One code for
   * both absences (missing row, foreign match's row): the caller's sentence is the same, and
   * naming a foreign row's existence would leak another match's data into this one's error.
   */
  | 'ERR_ANNOTATION_NOT_FOUND'
  // commands/coachCard.ts — build unit B-3. B spec §4.2, scenarios 20–32.
  /**
   * B §4.2 — the card command landed outside its context's draft window: a `halftime` card is
   * writable while state ∈ {halftime, fullTime} (a whistled-away draft is finished after the
   * match — §2.5's boundary behavior), a `fullTime` card at `fullTime` only. Deliberately not
   * `ERR_NOT_AT_HALFTIME`'s sibling: that code names one state, and the halftime card's window
   * spans two. §2.5's AbortController symmetry means this code should never surface on a capture
   * surface — a generate cancelled at the whistle never attempts its commit.
   */
  | 'ERR_CARD_WINDOW'
  /**
   * B §4.2 — context vs purpose: an advance scout has no halftime workflow (scenario 20), so
   * `advanceScout` + `halftime` is illegal at the command grain, not merely unrendered. The UI
   * never offers the entry; this is the engine's half of the scenario-20 negative ("no card is
   * creatable" — asserted, not assumed).
   */
  | 'ERR_CARD_CONTEXT'
  /**
   * B §4.2 — a finalized card is immutable **and can never be discarded** (scenarios 29–30: it
   * persists with the match forever). One code for all three writers: an upsert over a final
   * card, a second finalize, and a discard of a final card are the same sentence to the user.
   */
  | 'ERR_CARD_FINALIZED'
  /**
   * B §4.2 — finalize or discard named a card that does not exist **on this match**. One code
   * for both absences (missing row, foreign match's row), the `ERR_ANNOTATION_NOT_FOUND`
   * argument: the sentence is the same, and naming a foreign row would leak another match's data.
   */
  | 'ERR_CARD_NOT_FOUND'
  /**
   * B §4.2 — the draft's **adjustments** breached a bound: an entry's text over
   * `COACH_ADJUSTMENT_MAX_CHARS`, or more than `COACH_CARD_MAX_ENTRIES` entries.
   *
   * **Scoped to adjustments ONLY.** `selectedHypothesisIds` is deliberately unbounded —
   * scenario 23's "zero to all, no cap" is a requirement whose negative (the old three-talk-point
   * limit does not exist in B) is asserted in verification, so no branch anywhere may count the
   * selection against this code.
   */
  | 'ERR_CARD_TOO_LARGE'
  /**
   * B §4.2 — the draft names a hypothesis this match does not have: a selection id or an
   * adjustment's `sourceHypothesisIds` entry pointing at a deleted or foreign-match row. Refused
   * rather than filtered, because a card citing "because —" sources that vanish silently is the
   * blank-line failure §3.2 names for talk points, one workflow over.
   */
  | 'ERR_CARD_SOURCE'
  /**
   * B §4.2 — finalize on a card with nothing that would render: no accepted entry and no
   * surviving manual one. "Make the card (n)" is guarded in the UI; this is the engine's half —
   * a card of only unaccepted suggestions must be impossible, or the scenario-29 negative
   * ("rejected/suggested entries absent from the card") could be satisfied by an empty card.
   */
  | 'ERR_CARD_EMPTY'
  /**
   * B §6 / F-1 (build unit B-18) — `CompleteMatch` was refused because the match still holds a
   * **draft** card.
   *
   * Every card window excludes `archived` (`commands/coachCard.ts`'s `DRAFT_WINDOW`), so a match
   * archived over a draft would strand it uneditable and undiscardable forever. The resolution is
   * one tap either way — finish the card, or discard the draft — which is why this refuses rather
   * than deleting or silently finalizing on the user's behalf: both of those decide, for the user,
   * the one thing the draft's existence says they had not yet decided.
   *
   * A *finalized* card never blocks. Cards are optional (scenarios 21/27); only unfinished
   * business is.
   */
  | 'ERR_CARD_DRAFTS'
  // commands/settings.ts — build unit B-4. B spec §4.2, F-5.
  /**
   * B §4.2 (F-5) — `UpdateSetting` named a key outside the closed `SettingKey` union. The
   * settings table's one interesting property is that closed key space (`types/index.ts` — every
   * key has a declared publish-or-deny archive disposition), and this code is the runtime half of
   * keeping it closed: TypeScript cannot vouch for a key that arrived through view state or a
   * future caller, so the handler re-checks against `SETTING_KEYS` and refuses anything it does
   * not recognise — a write is deliberate or it does not happen.
   */
  | 'ERR_SETTING_KEY';

export type DomainResult<T> =
  | {
      readonly ok: true;
      readonly value: T;
      readonly log: readonly DomainLogEntry[];
    }
  | {
      readonly ok: false;
      readonly code: DomainErrorCode;
      readonly message: string;
      readonly log: readonly DomainLogEntry[];
    };

/** A success. `log` defaults to empty — the happy path is not required to say anything. */
export function ok<T>(value: T, log: readonly DomainLogEntry[] = []): DomainResult<T> {
  return { ok: true, value, log };
}

/**
 * The only way to build a rejection — and it writes the OBS-1 entry for you.
 *
 * @param module   OBS-1's module category, e.g. `'machine'`
 * @param matchId  OBS-1's correlation id; `null` when no match exists yet (`idle` → CreateMatch)
 * @param code     the typed category the caller branches on
 * @param message  plain language, for the non-blocking surface the UI shows (§2.2)
 * @param data     structured context — never a formatted string, so it stays queryable
 */
export function reject<T>(
  module: string,
  matchId: string | null,
  code: DomainErrorCode,
  message: string,
  data: Record<string, unknown> = {},
): DomainResult<T> {
  return {
    ok: false,
    code,
    message,
    log: [{ module, level: 'warn', matchId, msg: `${code}: ${message}`, data: { ...data, code } }],
  };
}
